import React, { useState, useEffect } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { GraduationCap, Users, CheckCircle, Mail, TrendingUp, Calendar } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EmailCourseStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { getAuthHeaders } = useAuth();

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API}/admin/emails/course-stats`, { headers });
      setStats(response.data.stats);
    } catch (error) {
      console.error('Failed to fetch course stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getDayColors = ['#ea580c', '#f97316', '#fb923c', '#fdba74', '#fed7aa', '#ffedd5', '#fff7ed'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-12 text-gray-500">
        <GraduationCap className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p>No course data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white">7-Day Email Course Stats</h2>
        <Badge className="bg-orange-500/20 text-orange-400 px-3 py-1">
          <Mail className="h-4 w-4 mr-1 inline" />
          {stats.course_emails_sent} emails sent
        </Badge>
      </div>

      {/* Main Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wider">Total Enrolled</p>
                <p className="text-3xl font-bold text-white mt-1">{stats.total_enrollments}</p>
              </div>
              <div className="p-3 rounded-full bg-blue-500/20">
                <Users className="h-6 w-6 text-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wider">Active</p>
                <p className="text-3xl font-bold text-white mt-1">{stats.active_enrollments}</p>
              </div>
              <div className="p-3 rounded-full bg-orange-500/20">
                <TrendingUp className="h-6 w-6 text-orange-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wider">Completed</p>
                <p className="text-3xl font-bold text-white mt-1">{stats.completed_enrollments}</p>
              </div>
              <div className="p-3 rounded-full bg-green-500/20">
                <CheckCircle className="h-6 w-6 text-green-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wider">Today</p>
                <p className="text-3xl font-bold text-white mt-1">{stats.today_enrollments}</p>
              </div>
              <div className="p-3 rounded-full bg-purple-500/20">
                <Calendar className="h-6 w-6 text-purple-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Completion Rate Progress */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <GraduationCap className="h-5 w-5 text-orange-500" />
            Course Completion Rate
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Progress</span>
              <span className="text-white font-bold">{stats.completion_rate}%</span>
            </div>
            <Progress value={stats.completion_rate} className="h-3 bg-gray-700" />
            <p className="text-xs text-gray-500 mt-2">
              {stats.completed_enrollments} of {stats.total_enrollments} students have completed all 7 days
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Day Distribution Chart */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Students by Current Day</CardTitle>
          </CardHeader>
          <CardContent>
            {stats.day_distribution.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={stats.day_distribution}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis 
                      dataKey="day" 
                      stroke="#9ca3af" 
                      fontSize={12}
                      tickFormatter={(day) => `Day ${day}`}
                    />
                    <YAxis stroke="#9ca3af" fontSize={12} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1f2937',
                        border: '1px solid #374151',
                        borderRadius: '8px',
                        color: '#fff'
                      }}
                      formatter={(value, name, props) => [`${value} students`, `Day ${props.payload.day}`]}
                    />
                    <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                      {stats.day_distribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={getDayColors[index % getDayColors.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-gray-500">
                No active enrollments
              </div>
            )}
          </CardContent>
        </Card>

        {/* Enrollment Trend */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Enrollment Trend (14 days)</CardTitle>
          </CardHeader>
          <CardContent>
            {stats.enrollments_trend.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={stats.enrollments_trend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis 
                      dataKey="date" 
                      stroke="#9ca3af" 
                      fontSize={12}
                      tickFormatter={(date) => {
                        const d = new Date(date);
                        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                      }}
                    />
                    <YAxis stroke="#9ca3af" fontSize={12} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1f2937',
                        border: '1px solid #374151',
                        borderRadius: '8px',
                        color: '#fff'
                      }}
                      labelFormatter={(date) => new Date(date).toLocaleDateString()}
                      formatter={(value) => [`${value} enrollments`, 'New']}
                    />
                    <Line
                      type="monotone"
                      dataKey="count"
                      stroke="#ea580c"
                      strokeWidth={2}
                      dot={{ fill: '#ea580c', strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-gray-500">
                No enrollment data
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Enrollments */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white">Recent Enrollments</CardTitle>
        </CardHeader>
        <CardContent>
          {stats.recent_list.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Student</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Current Day</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Status</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Enrolled</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recent_list.map((enrollment, index) => (
                    <tr key={index} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                      <td className="py-3 px-4">
                        <div>
                          <p className="text-white text-sm">{enrollment.name || 'Driver'}</p>
                          <p className="text-gray-500 text-xs">{enrollment.email}</p>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <Badge className="bg-orange-500/20 text-orange-400">
                          Day {enrollment.current_day}
                        </Badge>
                      </td>
                      <td className="py-3 px-4">
                        {enrollment.completed ? (
                          <span className="flex items-center gap-1 text-green-400 text-sm">
                            <CheckCircle className="h-4 w-4" />
                            Completed
                          </span>
                        ) : (
                          <span className="text-blue-400 text-sm">In Progress</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-gray-400 text-sm">
                        {formatDate(enrollment.enrolled_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No enrollments yet</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailCourseStats;
